<script lang="ts" setup>
import { getBuilders, type GetBuildersBuilderResponse, getDirectoryCategories, type GetDirectoryCategoriesDirectoryCategoryResponse } from '@/api';
import { ChevronDownIcon } from '@heroicons/vue/20/solid';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useHead } from '@unhead/vue';
import { type LocationQueryRaw, useRoute, useRouter } from 'vue-router';
import { getCanonicalUrl, parseNumber } from '@/utilities';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const canonicalUrl = getCanonicalUrl(route.path);

const builders = ref<GetBuildersBuilderResponse[]>();
const directoryCategories = ref<GetDirectoryCategoriesDirectoryCategoryResponse[]>();
const filterText = ref('');
const filterDirectoryCategoryId = ref(2);

// State management for URL synchronization
let isNavigating = false;
let isSyncingFromRoute = false;
const isInitialized = ref(false);

// Normalize function for case/accent-insensitive matching
const normalize = (str: string) =>
  str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const fetchDirectoryCategories = async () => {
  const response = await getDirectoryCategories();
  if (!response.response?.ok || !response.data) {
    return;
  }
  directoryCategories.value = response.data.items;
};

const fetchBuilders = async () => {
  const response = await getBuilders();
  if (!response.response?.ok || !response.data) {
    return;
  }
  builders.value = response.data.items;
};

// Sync filter from URL query parameters
const fromQuery = (query: LocationQueryRaw) => {
  isSyncingFromRoute = true;

  try {
    if (query.categoryId) {
      const categoryId = parseNumber(query.categoryId);
      if (categoryId && categoryId !== filterDirectoryCategoryId.value) {
        filterDirectoryCategoryId.value = categoryId;
      }
    }
  } finally {
    void nextTick(() => {
      isSyncingFromRoute = false;
    });
  }
};

// Sync filter to URL query parameters
const toQuery = (): Record<string, string | number> | undefined => {
  const newQuery: Record<string, string | number> = {};
  let anySet = false;

  if (filterDirectoryCategoryId.value !== 2) {
    // Only add if not default value
    newQuery.categoryId = filterDirectoryCategoryId.value;
    anySet = true;
  }

  return anySet ? newQuery : undefined;
};

// Navigate and update URL
const navigateToDirectory = async (force = false) => {
  if (!force && isNavigating) return;

  isNavigating = true;
  try {
    const currentRoute = router.currentRoute.value;
    const query = toQuery();

    if (currentRoute.name === 'directory') {
      // If already on directory page, just update query
      const currentQuery = JSON.stringify(currentRoute.query);
      const nextQuery = JSON.stringify(query || {});
      if (currentQuery !== nextQuery) {
        await router.replace({ name: 'directory', query });
      }
    }
  } finally {
    isNavigating = false;
  }
};

// Filtered builders based on input
const filteredBuilders = computed(() => {
  if (!builders.value) {
    return [];
  }

  const normalizedText = normalize(filterText.value);
  const directoryCategoryId = filterDirectoryCategoryId.value;

  return builders.value.filter((builder) => builder.category.id === directoryCategoryId && normalize(builder.name).includes(normalizedText));
});

// Group filtered builders by the first letter
const groupedBuilders = computed(() => {
  const groups: Record<string, GetBuildersBuilderResponse[]> = {};

  for (const builder of filteredBuilders.value) {
    if (!builder.name[0]) continue;

    const normalizedChar = builder.name[0]
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase();

    const groupKey = /^[A-Z]$/.test(normalizedChar) ? normalizedChar : '#';

    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }

    groups[groupKey].push(builder);
  }

  return groups;
});

// Initialize data and setup watchers
onMounted(async () => {
  isSyncingFromRoute = true;

  await fetchDirectoryCategories();
  await fetchBuilders();

  // Hydrate from URL query parameters if on directory route
  if (route.name === 'directory') {
    fromQuery(route.query);
  }

  await nextTick(() => {
    isSyncingFromRoute = false;
    isInitialized.value = true;
  });
});

// Clear text filter when category changes
watch(filterDirectoryCategoryId, () => {
  filterText.value = '';
});

// Watch for route changes to sync from URL
watch(
  () => route.fullPath,
  () => {
    if (route.name === 'directory') {
      fromQuery(route.query);
    }
  }
);

// Watch for filter changes to update URL
watch(filterDirectoryCategoryId, async () => {
  if (isInitialized.value && !isNavigating && !isSyncingFromRoute) {
    await navigateToDirectory();
  }
});

useHead({
  title: t('views.directory.title'),
  meta: [
    {
      name: 'description',
      content: t('views.home.meta.description'),
    },
    { property: 'og:title', content: t('views.directory.title') },
    { property: 'og:description', content: t('views.home.meta.description') },
    { property: 'og:url', content: canonicalUrl },
    { name: 'twitter:title', content: t('views.directory.title') },
    { name: 'twitter:description', content: t('views.home.meta.description') },
    { name: 'twitter:card', content: 'summary' },
  ],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: t('views.directory.title'),
        url: canonicalUrl,
      }),
    },
  ],
});
</script>

<template>
  <h1 class="mt-6 mb-6 text-3xl font-semibold tracking-tight">
    {{ t('views.directory.header') }}
  </h1>

  <!-- Filter Textbox -->
  <div class="no-print mb-6">
    <div class="grid grid-cols-3 gap-x-4">
      <div class="grid grid-cols-1">
        <select
          v-model="filterDirectoryCategoryId"
          class="focus:outline-ph-green-600 col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2"
        >
          <option v-for="directoryCategory in directoryCategories" :key="directoryCategory.id" :value="directoryCategory.id">{{ directoryCategory.name }}</option>
        </select>
        <ChevronDownIcon aria-hidden="true" class="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-500 sm:size-4" />
      </div>
      <div>
        <input v-model="filterText" class="focus:border-ph-green-500 w-full rounded border border-gray-300 px-4 py-2 text-base focus:ring focus:outline-none" placeholder="Chercher..." type="text" />
      </div>
    </div>
  </div>

  <!-- Builders by Letter Section -->
  <div v-if="Object.keys(groupedBuilders).length > 0">
    <div v-for="letter in Object.keys(groupedBuilders).sort()" :key="letter" class="mb-12">
      <!-- Section Header with underline -->
      <div class="mb-4 border-b border-gray-300 pb-1">
        <h2 class="text-2xl font-bold tracking-wide text-gray-800">
          {{ letter }}
        </h2>
      </div>

      <!-- Builders Grid -->
      <ul class="mx-auto grid max-w-2xl grid-cols-2 gap-x-4 gap-y-8 text-center sm:grid-cols-3 md:grid-cols-4 lg:mx-0 lg:max-w-none lg:grid-cols-5 xl:grid-cols-6" role="list">
        <li v-for="builder in groupedBuilders[letter]" :key="builder.id">
          <!-- Fixed-size logo container -->
          <RouterLink :to="{ name: 'builder', params: { slug: builder.name.toSlug(), builderId: builder.id } }" class="group">
            <div class="rounded-lg p-4 group-hover:bg-gray-100">
              <div class="mx-auto flex h-24 w-24 items-center justify-center overflow-hidden rounded">
                <img v-if="builder.logo && builder.logo.length > 0" :alt="builder.name" :src="builder.logo.resizedToSmall(true)" class="max-h-full max-w-full object-contain" />
              </div>
              <h3 class="group-hover:text-ph-green-500 mt-4 text-base font-semibold tracking-tight text-gray-900">
                {{ builder.name }}
              </h3>
            </div>
          </RouterLink>
        </li>
      </ul>
    </div>
  </div>

  <!-- No builders message -->
  <div v-else class="mt-8 text-center text-gray-500">
    {{ t('views.directory.no-results') }}
  </div>
</template>
